'use client';

import Link from 'next/link';
import { Coins } from 'lucide-react';
import { useCreditWallet } from '@/lib/api/billing';
import { cn } from '@/lib/utils';

interface CreditBadgeProps {
  /** compact 모드: 숫자만 표시 */
  compact?: boolean;
  className?: string;
}

/**
 * 사이드바 또는 헤더에 삽입하는 크레딧 잔액 배지.
 * 잔액이 낮으면 빨간색으로 표시.
 */
export function CreditBadge({ compact = false, className }: CreditBadgeProps) {
  const { data: wallet, isLoading } = useCreditWallet();

  if (isLoading || wallet === null) { return null; }

  const balance = wallet?.balance ?? 0;
  // 음악 1곡 30크레딧 + 마진 기준 — 50 이하면 1곡 생성에 충분치 않음
  const isLow = balance <= 50;
  const isEmpty = balance === 0;

  return (
    <Link
      href="/settings/billing"
      data-testid="credit-badge"
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
        isEmpty
          ? 'bg-red-100 text-red-700 hover:bg-red-200'
          : isLow
          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
          : 'bg-purple-50 text-purple-700 hover:bg-purple-100',
        className
      )}
      title={`크레딧 잔액: ${balance}개`}
    >
      <Coins className="w-3.5 h-3.5 flex-shrink-0" />
      {!compact && <span>{balance.toLocaleString()}</span>}
      {compact && <span>{balance}</span>}
      {isEmpty && !compact && <span className="font-semibold">충전 필요</span>}
    </Link>
  );
}

export default CreditBadge;
